/**
 * /eleve/planning — Planning personnel de l'élève.
 * Combine : leçons planifiées par le secrétariat (lecture seule) + événements
 * personnels que l'élève s'ajoute lui-même (perso_events).
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Calendar, Clock, Plus, Pencil } from 'lucide-react';
import api from '../../api';
import PersonalEventModal from '../../components/PersonalEventModal';

const TYPE_LABELS = {
  plateau: 'Plateau', circulation: 'Circulation', code: 'Code / Révision',
  salle: 'Cours en salle', autre: 'Autre',
};

export default function ElevePlanningPage() {
  const [linked, setLinked] = useState([]);
  const [persoEvents, setPersoEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const d = await api.boEleveDashboard();
      setLinked(d?.upcoming || []);
    } catch (_) { /* silent : élève non lié à une fiche secrétariat */ }
    try {
      const p = await api.persoListEvents();
      setPersoEvents(p?.items || []);
    } catch (_) { /* silent */ }
    setLoading(false);
  }, []);

  useEffect(() => {
    document.title = 'Mon planning · Élève';
    refresh();
  }, [refresh]);

  const openNew = () => { setEditing(null); setModalOpen(true); };
  const openEdit = (ev) => { setEditing(ev); setModalOpen(true); };

  return (
    <div className="max-w-4xl" data-testid="eleve-planning-page">
      <div className="flex items-start justify-between gap-3 mb-6">
        <div>
          <div className="text-[10.5px] font-black uppercase tracking-[0.22em] text-rose-500 mb-2">Mes rendez-vous</div>
          <h1 className="text-4xl sm:text-5xl font-black text-white tracking-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Mon planning</h1>
          <p className="text-sm text-white/60 mt-2">Leçons planifiées par le secrétariat + cours perso que tu ajoutes toi-même.</p>
        </div>
        <button onClick={openNew} data-testid="btn-add-perso-event" className="shrink-0 inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-rose-600 hover:bg-rose-700 text-white text-sm font-black shadow-lg shadow-rose-900/30 transition">
          <Plus size={14} /> Ajouter
        </button>
      </div>

      {loading && <div className="text-white/40 text-sm">Chargement…</div>}

      {!loading && linked.length > 0 && (
        <section className="mb-6">
          <h2 className="text-[10px] font-black uppercase tracking-widest text-emerald-400 mb-2">Planifié par l&apos;auto-école</h2>
          <div className="space-y-2">
            {linked.map(l => (
              <div key={l.id} className="rounded-xl border border-emerald-500/20 bg-emerald-500/[0.04] p-4 flex items-center gap-4">
                <div className="w-1 h-12 rounded-full bg-emerald-500" />
                <div className="flex-1 min-w-0">
                  <div className="text-white font-semibold text-sm capitalize">
                    {new Date(l.date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
                  </div>
                  <div className="text-white/60 text-xs mt-0.5 flex items-center gap-2">
                    <Clock size={11} /> {(l.start_time || '').slice(0, 5)} · {l.duration_min} min · {l.teacher_name || 'Enseignant'}
                  </div>
                </div>
                <div className="text-[10px] px-2 py-1 rounded-full bg-emerald-500/15 text-emerald-300 font-black uppercase tracking-widest">{l.type || 'Conduite'}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      <section>
        <h2 className="text-[10px] font-black uppercase tracking-widest text-rose-400 mb-2">Mes cours perso</h2>
        {persoEvents.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-white/15 bg-white/[0.02] p-8 text-center" data-testid="perso-empty">
            <Calendar className="w-8 h-8 text-white/30 mx-auto mb-2" />
            <div className="text-white/60 text-sm">Aucun cours perso pour le moment.</div>
            <button onClick={openNew} className="mt-3 inline-flex items-center gap-1.5 text-rose-400 hover:text-rose-300 text-sm font-semibold">
              <Plus size={14} /> Ajouter mon premier cours
            </button>
          </div>
        ) : (
          <div className="space-y-2" data-testid="perso-events-list">
            {persoEvents.map(ev => (
              <button key={ev.id} onClick={() => openEdit(ev)} className="w-full text-left rounded-xl border border-rose-500/20 bg-rose-500/[0.03] hover:bg-rose-500/[0.06] p-4 flex items-center gap-4 transition" data-testid={`perso-event-${ev.id}`}>
                <div className="w-1 h-12 rounded-full bg-rose-500" />
                <div className="flex-1 min-w-0">
                  <div className="text-white font-semibold text-sm truncate">{ev.title}</div>
                  <div className="text-white/60 text-xs mt-0.5 flex items-center gap-2">
                    <Clock size={11} /> {new Date(ev.date).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })}{ev.start_time && ` · ${ev.start_time.slice(0, 5)}`} · {ev.duration_min} min
                    {ev.contact_name && ` · ${ev.contact_name}`}
                  </div>
                </div>
                <div className="text-[10px] px-2 py-1 rounded-full bg-rose-500/15 text-rose-300 font-black uppercase tracking-widest">{TYPE_LABELS[ev.subject] || 'Cours'}</div>
                <Pencil size={13} className="text-white/30" />
              </button>
            ))}
          </div>
        )}
      </section>

      <PersonalEventModal open={modalOpen} onClose={() => setModalOpen(false)} event={editing} mode="student" onSaved={refresh} />
    </div>
  );
}
